import React, { useState, useEffect, useCallback, useRef } from "react";
import { getInsights, updateInsightStatus, refreshInsights } from "../api/insightsClient";
import { formatCurrency, formatCurrencyCompact } from "../utils/formatters";

function DollarIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
    );
}

function RepeatIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <polyline points="17 1 21 5 17 9" />
            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <polyline points="7 23 3 19 7 15" />
            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
    );
}

function ShieldIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
    );
}

function TrendingDownIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
            <polyline points="16 17 22 17 22 11" />
        </svg>
    );
}

function RefreshIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
    );
}

function getInsightIcon(type) {
    switch (type) {
        case "IDLE_CASH":
            return { icon: DollarIcon };
        case "RECURRING_CHARGES":
            return { icon: RepeatIcon };
        case "EMERGENCY_FUND_LOW":
            return { icon: ShieldIcon };
        case "EMERGENCY_FUND_HIGH":
            return { icon: ShieldIcon };
        case "SAVINGS_RATE_DROP":
            return { icon: TrendingDownIcon };
        default:
            return { icon: DollarIcon };
    }
}

export default function InsightsPage() {
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const abortRef = useRef(null);

    const fetchInsights = useCallback(async () => {
        if (abortRef.current) {
            abortRef.current.abort();
        }
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        setError(null);
        try {
            const data = await getInsights(controller.signal);
            setInsights(data.insights || []);

            (data.insights || []).forEach((insight) => {
                if (insight.status === "NEW") {
                    updateInsightStatus(insight.id, "VIEWED").catch(() => {});
                }
            });
        } catch (err) {
            if (err.name !== "CanceledError" && err.name !== "AbortError") {
                setError(err.message || "Failed to load insights");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchInsights();
        return () => {
            if (abortRef.current) {
                abortRef.current.abort();
            }
        };
    }, [fetchInsights]);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await refreshInsights();
            await fetchInsights();
        } catch (err) {
            setError(err.message || "Failed to refresh insights");
        } finally {
            setRefreshing(false);
        }
    };

    const handleDismiss = async (id) => {
        try {
            await updateInsightStatus(id, "DISMISSED");
            setInsights((prev) => prev.filter((i) => i.id !== id));
        } catch (err) {
            // silently fail
        }
    };

    const totalImpact = insights.reduce((sum, i) => sum + (i.dollarImpact || 0), 0);

    const Header = ({ children }) => (
        <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 tracking-tight">Insights</h1>
            {children}
        </div>
    );

    const RefreshButton = () => (
        <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary text-sm"
        >
            <RefreshIcon className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
        </button>
    );

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto">
                <Header />
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="card h-32 animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto">
                <Header />
                <div className="card p-6">
                    <p className="text-danger-500 mb-4">{error}</p>
                    <button onClick={fetchInsights} className="btn-primary">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (insights.length === 0) {
        return (
            <div className="max-w-7xl mx-auto">
                <Header><RefreshButton /></Header>
                <div className="card p-12 text-center">
                    <p className="text-gray-500">
                        No insights yet. Connect your accounts and we'll analyze your finances for opportunities.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto">
            <Header><RefreshButton /></Header>

            {/* Summary banner */}
            <div className="card p-6 mb-6">
                <p className="text-gray-600 text-sm">
                    We found {insights.length} opportunit{insights.length !== 1 ? "ies" : "y"} worth an estimated
                </p>
                <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-gray-900 text-3xl font-semibold tracking-tight">
                        {formatCurrencyCompact(totalImpact)}
                    </span>
                    <span className="text-gray-500">/year</span>
                </div>
            </div>

            {/* Insight cards */}
            <div className="space-y-4">
                {insights.map((insight) => {
                    const { icon: Icon } = getInsightIcon(insight.type);
                    return (
                        <div
                            key={insight.id}
                            className={`card p-6 relative ${insight.status === "NEW" ? "border-l-2 border-l-accent-500" : ""}`}
                        >
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-700 shrink-0">
                                    <Icon className="w-5 h-5" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-3">
                                        <h3 className="text-gray-900 font-semibold">{insight.title}</h3>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {insight.dollarImpact != null && (
                                                <div className="bg-accent-50 text-accent-600 text-sm font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                                                    +{formatCurrencyCompact(insight.dollarImpact)}/year
                                                </div>
                                            )}
                                            <button
                                                onClick={() => handleDismiss(insight.id)}
                                                className="text-gray-400 hover:text-gray-700 p-1"
                                                aria-label="Dismiss"
                                            >
                                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                                                    <line x1="18" y1="6" x2="6" y2="18" />
                                                    <line x1="6" y1="6" x2="18" y2="18" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-gray-600 text-sm mt-1">{insight.description}</p>

                                    {insight.action && (
                                        <div className="bg-gray-50 rounded-lg p-3 mt-3 text-gray-700 text-sm">
                                            {insight.action}
                                        </div>
                                    )}

                                    {insight.type === "RECURRING_CHARGES" && insight.metadata?.subscriptions?.length > 0 && (
                                        <table className="mt-3 w-full text-sm">
                                            <thead>
                                                <tr className="text-gray-500 text-left">
                                                    <th className="pb-2 font-medium">Merchant</th>
                                                    <th className="pb-2 font-medium text-right">Amount</th>
                                                    <th className="pb-2 font-medium text-right">Last charged</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {insight.metadata.subscriptions.map((sub, idx) => (
                                                    <tr key={idx} className="text-gray-700">
                                                        <td className="py-1">{sub.merchant}</td>
                                                        <td className="py-1 text-right">{formatCurrency(sub.amount)}</td>
                                                        <td className="py-1 text-right">{sub.lastDate}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
